// apps/admin/src/components/ui/BackLink.tsx
//
// Standardized "back to list" affordance for detail pages.
// Renders a lucide ArrowLeft icon + label, navigates via react-router.
//
// Usage:
//   <BackLink to="/merchants" label="Quay lại Merchants" />
//   <BackLink onClick={() => navigate(-1)} label="Quay lại" />

import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

interface BackLinkProps {
  /** Target route. If omitted, falls back to navigate(-1). */
  to?: string;
  /** Override onClick. Takes precedence over `to`. */
  onClick?: () => void;
  /** Vietnamese label shown next to the icon. */
  label?: string;
  className?: string;
  testId?: string;
}

export function BackLink({
  to,
  onClick,
  label,
  className,
  testId = 'detail-back-link',
}: BackLinkProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const resolvedLabel = label ?? t('ui.backLink.back');
  const handleClick = (): void => {
    if (onClick) {
      onClick();
      return;
    }
    if (to) {
      navigate(to);
      return;
    }
    navigate(-1);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={testId}
      className={cn(
        'inline-flex w-fit items-center gap-1.5 text-sm font-medium text-pw-text-muted hover:text-pw-text transition-colors',
        className,
      )}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      <span>{resolvedLabel}</span>
    </button>
  );
}

export default BackLink;
